import { languageDisplayCode, LanguagesEnum } from '@/api/enums';
import { sortLanguagesWithPriority } from '@/utils/helpers';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { FlexContainer } from '../styled-ui';

const LanguageTab = styled.div`
  position: relative;
  display: flex;
  width: 40px;
  min-width: 40px;
  padding: 2px 9px;
  justify-content: center;
  align-items: center;
  gap: 3px;
  border-radius: 200px;
  background: #efefed;
  border: 1px solid #efefed;
  color: #000;
  font-family: Montserrat;
  font-size: 11px;
  font-weight: 500;
  line-height: 150%;

  &.selected {
    background: var(--beige-main);
    border: 1px solid #b1b1af;
  }

  &.valid:after,
  &.required:after,
  &.fallback:after {
    font-family: var(--icon-font-family);
    display: inline-block;
    font-size: 7px;
    line-height: 7px;
    font-weight: 900;
  }

  &.required::after {
    content: '\f00d';
    color: var(--red-main);
  }

  &.fallback::after {
    content: '\f129';
    color: var(--blue-dark);
  }

  &.valid::after {
    content: '\f00c';
    color: var(--green-main);
  }

  &.disabled {
    pointer-events: none;
    opacity: 80%;
  }
`;

const ExpandButton = styled.span`
  font-size: var(--xsmall-font-size);
  line-height: var(--small-line-height);
  color: var(--black-main);
  cursor: pointer;
  z-index: 999;

  i {
    color: var(--gray-dark);
    font-size: var(--xsmall-font-size);
    line-height: var(--small-line-height);
    margin-left: 6px;
  }
`;

type LanguageTabsProps = {
  options: LanguagesEnum[];
  limitVisible?: number;
  onToggle?: (type?: string) => Promise<void> | void;
  defaultSelected?: string;
  validOptions?: string[];
  requiredOptions?: string[];
  fallbackOptions?: string[];
  isDisabled?: boolean;
  resetVisibility?: boolean;
};

export const LanguageTabs: React.FC<LanguageTabsProps> = ({
  options,
  limitVisible = 0,
  onToggle,
  defaultSelected,
  validOptions = [],
  requiredOptions = [],
  fallbackOptions = [],
  isDisabled = false,
  resetVisibility,
}) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<string>(
    languageDisplayCode[defaultSelected as LanguagesEnum],
  );
  const [sortedOptions, setSortedOptions] = useState<string[]>([]);
  const [isExpanded, setIsExpanded] = useState(false);

  // Only set initial selected state, don't reset on every defaultSelected change
  useEffect(() => {
    if (!selected && defaultSelected) {
      setSelected(defaultSelected);
    } else if (!selected && options.length > 0) {
      setSelected(languageDisplayCode[options[0] as LanguagesEnum]);
    }
  }, [defaultSelected, options, selected]);

  useEffect(() => {
    setIsExpanded(false);
  }, [resetVisibility]);

  useEffect(() => {
    setSortedOptions(
      sortLanguagesWithPriority(
        options?.map(
          (languageCode: string) => languageDisplayCode[languageCode],
        ),
      ),
    );
  }, [options]);

  const [visibleLanguages, setVisibleLanguages] = useState<string[]>([]);

  useEffect(() => {
    if (!sortedOptions) {
      setVisibleLanguages([]);
      return;
    }

    if (isExpanded || !limitVisible) {
      setVisibleLanguages(sortedOptions);
    } else {
      setVisibleLanguages(sortedOptions.slice(0, limitVisible));
    }
  }, [sortedOptions, isExpanded, limitVisible]);

  return (
    <FlexContainer gap={5} justify="flex-start" wrap="wrap">
      {visibleLanguages?.map((option) => (
        <LanguageTab
          id={`language-tab-${option}`}
          key={option}
          className={`${selected === option ? 'selected' : ''}
          ${validOptions.includes(option) ? 'valid' : ''}
          ${requiredOptions.includes(option) ? 'required' : ''}
          ${fallbackOptions.includes(option) ? 'fallback' : ''}
          ${isDisabled ? 'disabled' : ''}
          ${onToggle ? 'cursor-pointer' : ''}`}
          onClick={
            onToggle
              ? async () => {
                  if (selected !== option) {
                    setSelected(option);
                    await onToggle(option);
                  }
                }
              : undefined
          }
        >
          {t(option)}
        </LanguageTab>
      ))}
      {options.length - limitVisible > 0 && !!limitVisible && (
        <ExpandButton onClick={() => setIsExpanded((prev) => !prev)}>
          {isExpanded ? (
            <i className="pi pi-chevron-up" />
          ) : (
            `+${options.length - limitVisible} more`
          )}
        </ExpandButton>
      )}
    </FlexContainer>
  );
};
